import { contrastRatio } from "@/color/contrast";
import { brightsFor, type Palette } from "@/color/generate";
import { type Hex, mix, shade, toLch } from "@/color/oklch";
import { resolveTheme } from "@/model/resolve";
import { newId, SCHEMA_URL, SCHEMA_VERSION, type Theme } from "@/model/theme";
import type { Ref, TokenId, Variant } from "@/model/tokens";

/** What every terminal color format can express. */
export interface TerminalColors {
  name: string;
  background: Hex;
  foreground: Hex;
  /** ANSI 0..15; missing entries are derived. */
  ansi: (Hex | undefined)[];
  cursor?: Hex;
  cursorText?: Hex;
  selectionBg?: Hex;
  selectionFg?: Hex;
}

/**
 * Builds a theme from a terminal palette. The palette gets sensible neutrals and
 * accents; every imported terminal color that the defaults would not reproduce is kept
 * as an override, so the terminal looks exactly like the source.
 */
export function themeFromTerminal(src: TerminalColors): Theme {
  const bg = src.background;
  const fg = src.foreground;
  const variant: Variant = toLch(bg).l < 0.6 ? "dark" : "light";
  const a = (i: number, fallback: Hex): Hex => src.ansi[i] ?? fallback;

  const red = a(1, "#cc3333");
  const green = a(2, "#33aa55");
  const yellow = a(3, "#ccaa33");
  const blue = a(4, "#3377cc");
  const magenta = a(5, "#aa55aa");
  const cyan = a(6, "#33aaaa");
  const dark = variant === "dark";
  // Bright variants are usually the "vivid" ones; prefer them as syntax accents in dark themes.
  const pick = (normal: Hex, i: number): Hex => {
    const bright = src.ansi[i];
    if (!bright) return normal;
    return contrastRatio(bright, bg) > contrastRatio(normal, bg) ? bright : normal;
  };

  const accents = {
    red: dark ? pick(red, 9) : red,
    green: dark ? pick(green, 10) : green,
    yellow: dark ? pick(yellow, 11) : yellow,
    blue: dark ? pick(blue, 12) : blue,
    purple: dark ? pick(magenta, 13) : magenta,
    cyan: dark ? pick(cyan, 14) : cyan,
  };
  const neutrals = {
    bg0: shade(bg, dark ? -0.03 : -0.03),
    bg1: bg,
    bg2: mix(bg, fg, 0.07),
    bg3: src.selectionBg ?? mix(bg, fg, 0.15),
    bg4: mix(bg, fg, 0.25),
    fg3: readable(src.ansi[8], bg, 2.5) ?? mix(bg, fg, 0.5),
    fg2: mix(bg, fg, 0.75),
    fg1: fg,
    fg0: (dark ? src.ansi[15] : src.ansi[0]) ?? fg,
  };
  const partial = {
    ...neutrals,
    ...accents,
    orange: mix(accents.red, accents.yellow, 0.45),
    pink: mix(accents.purple, accents.red, 0.35),
  };
  const palette = {
    ...partial,
    ...brightsFor(partial as Palette, variant),
    ...Object.fromEntries(
      (
        [
          ["brightRed", 9],
          ["brightGreen", 10],
          ["brightYellow", 11],
          ["brightBlue", 12],
          ["brightPurple", 13],
          ["brightCyan", 14],
        ] as const
      )
        .filter(([, i]) => src.ansi[i])
        .map(([k, i]) => [k, src.ansi[i]]),
    ),
  } as Palette;

  const now = new Date().toISOString();
  const theme: Theme = {
    $schema: SCHEMA_URL,
    version: SCHEMA_VERSION,
    id: newId(),
    name: src.name || "Imported theme",
    variant,
    palette,
    overrides: {},
    styles: {},
    locks: [],
    meta: { createdAt: now, updatedAt: now, basedOn: src.name },
  };

  const wanted: [TokenId, Hex | undefined][] = [
    ["terminal.cursor", src.cursor],
    ["terminal.cursorText", src.cursorText],
    ["terminal.selectionBg", src.selectionBg],
    ["terminal.selectionFg", src.selectionFg],
    ...src.ansi.map(
      (hex, i) => [`terminal.ansi${i}` as TokenId, hex] as [TokenId, Hex | undefined],
    ),
  ];
  const derived = resolveTheme(theme).colors;
  const overrides: Partial<Record<TokenId, Ref>> = {};
  for (const [id, hex] of wanted) if (hex && derived[id] !== hex) overrides[id] = hex;
  return { ...theme, overrides };
}

function readable(hex: Hex | undefined, bg: Hex, min: number): Hex | undefined {
  return hex && contrastRatio(hex, bg) >= min ? hex : undefined;
}
